import { z } from 'zod';

import {
  createEmptyLine,
  createEmptyPackage,
  sumLineQty,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/formSerialize';
import {
  mapPurchaseOrderLineToReceiptFormLine,
  purchaseOrderStockReceiptFormSchema,
  resolvePurchaseOrderLineSelectedUnitId,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapPurchaseOrderToStockReceiptForm';
import { ROUNDING_MODE, resolveRoundingMode } from '#/utils/roundingMode';

/** 排程倉優先；否則回退 PO 收貨倉（僅 UI 儲位 options）。 */
function resolveArrivalScheduleWarehouseId(schedule, poDetail) {
  return schedule?.warehouse?.id
    ?? schedule?.warehouseId
    ?? poDetail?.receiptWarehouse?.id
    ?? poDetail?.receiptWarehouseId
    ?? poDetail?.defaultWarehouse?.id
    ?? poDetail?.defaultWarehouseId
    ?? '';
}

function resolveLineRemainingQty(line, scheduleRemainingQty) {
  const fromLine = Number(line?.remainingQty ?? line?.plannedQty);
  if (!Number.isNaN(fromLine) && fromLine > 0) {
    return fromLine;
  }
  const fromSchedule = Number(scheduleRemainingQty);
  if (!Number.isNaN(fromSchedule) && fromSchedule > 0) {
    return fromSchedule;
  }
  return '';
}

/** 排程 detail line（或 PO line）→ 入庫表單列；首包 qty 預填剩餘／計劃量；單位在 line。 */
export function mapArrivalScheduleLineToReceiptFormLine(line, scheduleRemainingQty) {
  const fromPoShape = mapPurchaseOrderLineToReceiptFormLine(line);
  if (fromPoShape) {
    const qty = resolveLineRemainingQty(line, scheduleRemainingQty);
    return {
      ...fromPoShape,
      packages: [
        {
          ...createEmptyPackage(),
          qty,
        },
      ],
    };
  }

  const mtrId = line?.mtr?.id ?? line?.mtrId ?? line?.material?.id ?? '';
  if (!mtrId) {
    return null;
  }

  return createEmptyLine({
    mtrId,
    mtrNo: line?.mtr?.mtrNo ?? line?.material?.mtrNo ?? '',
    mtrName: line?.mtr?.name ?? line?.material?.name ?? '',
    unitId: resolvePurchaseOrderLineSelectedUnitId(line),
    unitOptions: line?.mtr?.unitOptions ?? line?.unitOptions ?? [],
    unitPrice: line?.unitPrice ?? '',
    remark: line?.remark ?? '',
    packages: [
      {
        ...createEmptyPackage(),
        qty: resolveLineRemainingQty(line, scheduleRemainingQty),
      },
    ],
  });
}

/**
 * 排程 detail＋PO detail → 建入庫 defaultValues。
 * 優先 schedule.lines；否則回退 PO lines（單列時 qty＝schedule.remainingQty）。
 */
export function buildArrivalScheduleStockReceiptDefaultValues(schedule, poDetail) {
  const scheduleRemaining = schedule?.remainingQty;
  let lines;

  if (Array.isArray(schedule?.lines) && schedule.lines.length > 0) {
    lines = schedule.lines
      .map((line) => mapArrivalScheduleLineToReceiptFormLine(line, scheduleRemaining))
      .filter(Boolean);
  } else {
    lines = (poDetail?.lines ?? [])
      .map((line) => mapArrivalScheduleLineToReceiptFormLine(
        line,
        (poDetail?.lines?.length === 1) ? scheduleRemaining : '',
      ))
      .filter(Boolean);
  }

  return {
    receiptDate: '',
    expectedArrivalDate: schedule?.expectedArrivalDate ?? '',
    warehouseId: resolveArrivalScheduleWarehouseId(schedule, poDetail),
    supplierId: poDetail?.supplier?.id ?? poDetail?.supplierId ?? '',
    currencyId: poDetail?.currencyId ?? poDetail?.currency?.id ?? '',
    roundingMode: resolveRoundingMode(
      poDetail?.roundingMode ?? poDetail?.amountSummary?.roundingMode,
      ROUNDING_MODE.HALF_UP,
    ),
    remark: '',
    issueReason: poDetail?.issueReason ?? '',
    customsDutyAmount: '',
    freightAmount: '',
    sourceType: 'PURCHASE',
    sourceId: poDetail?.id ?? '',
    lines,
  };
}

export function sumReceiptFormQty(values) {
  return (values?.lines ?? []).reduce(
    (sum, line) => sum + sumLineQty(line),
    0,
  );
}

/** IMS schema（倉別選填）＋合計 qty ≤ schedule.remainingQty。 */
export function buildArrivalScheduleReceiveFormSchema(remainingQtyMax) {
  const max = Number(remainingQtyMax);
  return purchaseOrderStockReceiptFormSchema.superRefine((data, ctx) => {
    if (Number.isNaN(max)) {
      return;
    }
    const total = sumReceiptFormQty(data);
    if (total > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `合計數量不可超過排程剩餘量 ${max}`,
        path: ['lines'],
      });
    }
  });
}
